import {
  ActionIcon,
  Badge,
  CloseButton,
  Flex,
  MantineSize,
  Transition,
  useMantineTheme,
} from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { useModals } from "@mantine/modals";
import { IconDotsVertical } from "@tabler/icons";
import { MouseEventHandler, useState } from "react";
import { FaLock, FaTrash, FaUnlockAlt } from "react-icons/fa";
import { RiPencilFill } from "react-icons/ri";
import { openActivateModal } from "./useCustomModal";
import { MESSAGES, ModalId } from "src/utils/constants";

export default function useTransitionMenu() {
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];
  const [activeOption, setActiveOption] = useState<undefined | { id: string }>(
    undefined
  );
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  const ShowActivation = (
    isActive: boolean,
    size?: MantineSize | undefined
  ) => {
    return (
      <Badge
        size={size}
        sx={{
          background: `${isActive ? "green" : "red"} !important`,
        }}
      >
        {isActive ? "Active" : "In Active"}
      </Badge>
    );
  };

  const TransitionMenu = ({
    onClick,
    onEdit,
    onDelete,
    onDeleteCustom,
    id,
    isActive = true,
    onUpdateActivation,
    fullWidth = false,
  }: {
    onClick: (item: any) => void;
    onEdit: MouseEventHandler<HTMLButtonElement>;
    onDelete?: (id: string) => void;
    onDeleteCustom?: () => void;
    id: string;
    isActive?: boolean;
    onUpdateActivation?: (id: string, isActive: boolean) => void;
    fullWidth?: boolean;
  }) => {
    const modals = useModals();
    const btnTheme = {
      backgroundColor: theme.colors.secondary[2],
      "&:hover": {
        backgroundColor: theme.colors.secondary[4],
      },
    };

    const openDeleteModal = (id: string, onDelete: (id: string) => void) =>
      modals.openContextModal("delete", {
        modalId: ModalId.DeleteTransition,
        closeOnClickOutside: false,
        title: MESSAGES.CONFIRM_DEL,
        centered: true,
        innerProps: {
          onDelete: () => onDelete(id),
        },
      });

    return (
      <>
        <ActionIcon
          size="lg"
          c={color}
          onClick={onClick}
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.secondary[4],
            },
          }}
        >
          <IconDotsVertical />
        </ActionIcon>

        <Transition
          mounted={activeOption?.id === id}
          transition="slide-left"
          duration={200}
          timingFunction="ease-in-out"
        >
          {(styles) => (
            <Flex
              bg={theme.colors.secondary[2]}
              justify="space-between"
              style={{
                ...styles,
                position: "absolute",
                right: 0,
                padding: "2px 4px",
                borderRadius: 4,
                ...(fullWidth && { inset: 0 }),
              }}
              align={"center"}
              ref={outsideClickRef}
            >
              {onUpdateActivation && (
                <ActionIcon
                  size="lg"
                  color={isActive ? "red.4" : "green"}
                  onClick={() =>
                    openActivateModal(id, isActive, onUpdateActivation)
                  }
                  sx={btnTheme}
                >
                  {isActive ? <FaLock size={17} /> : <FaUnlockAlt size={17} />}
                </ActionIcon>
              )}
              <ActionIcon
                color="red.4"
                onClick={() =>
                  onDeleteCustom
                    ? onDeleteCustom()
                    : openDeleteModal(id, onDelete!)
                }
                size="lg"
                sx={btnTheme}
              >
                <FaTrash />
              </ActionIcon>
              <ActionIcon size="lg" onClick={onEdit} sx={btnTheme}>
                <RiPencilFill size={21} />
              </ActionIcon>
              <CloseButton
                size="lg"
                c={theme.colors.light[5]}
                onClick={() => setActiveOption(undefined)}
                sx={{
                  backgroundColor: theme.colors.secondary[2],
                  "&:hover": {
                    backgroundColor: theme.colors.secondary[4],
                  },
                }}
              />
            </Flex>
          )}
        </Transition>
      </>
    );
  };

  return {
    TransitionMenu,
    setActiveOption,
    ShowActivation,
  };
}
